/*
  Root'd subscription buy box v6
  --------------------------------
  - Subscribe & Save and One-Time Purchase are ALWAYS-VISIBLE price cards;
    their prices, compare-ats and SAVE %s adapt to the selected supply size.
  - Supply sizes (30/60/90) live in an expandable panel and drive the theme
    picker's hidden pack radios, so the theme's variant engine (theme.Variants)
    stays the single source of truth for the master select[name=id], images,
    inventory, history state and dynamic checkout buttons. Flavor keeps living
    in the theme's own picker.
  - Subscribing maps the selected size to its cadence plan (30 -> monthly,
    60 -> every 2 months, 90 -> every 3 months) via a single hidden
    input[name=selling_plan] (set in sub mode, empty in one-time mode).
  - The theme's existing AJAX add-to-cart (theme.AjaxProduct -> /cart/add.js
    -> cart drawer) is untouched.
  - Optionally mirrors the current price into the Add to Cart button text.
*/
(function () {
  'use strict';

  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Map a Recharge selling-plan name to a month cadence. Tolerates hyphens
  // ("1-Month Supply") and phrasing differences across products.
  // Order matters: "Every Other Month" contains "month" too.
  // Deeper savings, deeper blue: tint the SAVE flag by % saved.
  function applySaveDepth(el, pct) {
    if (!el) return;
    el.classList.remove('buy-box__flag--save-mid', 'buy-box__flag--save-deep');
    if (pct >= 30) el.classList.add('buy-box__flag--save-deep');
    else if (pct >= 20) el.classList.add('buy-box__flag--save-mid');
  }

  // ---- Applied discount code -------------------------------------------
  // Priority 1: Shopify's own cart.discount_applications, rendered into
  //   data-promo-*. Authoritative - it IS the value Shopify will charge.
  // Priority 2: cold visitor with an EMPTY cart, so Shopify has nothing to
  //   allocate yet and Liquid renders no promo data. The code name comes
  //   from (in order): the ?promo=CODE query param, Shopify's own
  //   `discount_code` cookie (set by /discount/CODE links - THE fix for
  //   ad/popup links landing in a fresh session), or sessionStorage from a
  //   previous page this visit. The VALUE comes from the
  //   shop.metafields.custom.promo_codes JSON map rendered into
  //   data-promo-map. Once anything is in the cart, priority 1 takes over
  //   and the numbers are Shopify's own.
  // Any failure at any step returns null => no discount is applied anywhere
  // and the buy box renders exactly as it does today.
  function readCookie(name) {
    try {
      var parts = ('; ' + document.cookie).split('; ' + name + '=');
      if (parts.length < 2) return '';
      return decodeURIComponent(parts.pop().split(';')[0] || '').trim();
    } catch (e) { return ''; }
  }
  function readPromo(el) {
    try {
      var code = (el.dataset.promoCode || '').trim();
      var pct = parseFloat(el.dataset.promoValue || '0');
      // >=100% is a staff/test code; showing $0.00 in the buy box reads as a
      // bug, so leave those out of scope entirely.
      if (code && pct > 0 && pct < 100) return { code: code, pct: pct, source: 'cart' };

      // Priority 2a: a reward announced by Customer Luv Pop Up & Quiz
      // (sessionStorage 'clv_reward' {code,pct,exp}). It carries its own
      // percentage, so unique minted codes work with NO metafield entry.
      try {
        var rw = JSON.parse(sessionStorage.getItem('clv_reward') || 'null');
        if (rw && rw.code) {
          if (rw.exp && rw.exp <= Date.now()) {
            sessionStorage.removeItem('clv_reward');
          } else {
            var rp = parseFloat(rw.pct);
            if (rp > 0 && rp < 100) return { code: String(rw.code), pct: rp, source: 'reward' };
          }
        }
      } catch (e) { /* ignore */ }

      var fb = '';
      try {
        var qs = new URLSearchParams(window.location.search);
        fb = (qs.get('promo') || '').trim();
      } catch (e) { /* ignore */ }
      if (!fb) fb = readCookie('discount_code');
      try {
        if (fb) sessionStorage.setItem('rd_promo', fb);
        else fb = (sessionStorage.getItem('rd_promo') || '').trim();
      } catch (e) { /* private mode / blocked storage - ignore */ }
      if (!fb) return null;

      var map = {};
      try { map = JSON.parse(el.dataset.promoMap || 'null') || {}; } catch (e) { return null; }
      var hit = map[fb] || map[fb.toUpperCase()] || map[fb.toLowerCase()];
      if (!hit || String(hit.type).toLowerCase() !== 'percentage') return null;
      var v = parseFloat(hit.value);
      if (!(v > 0) || v >= 100) return null;
      return { code: fb, pct: v, source: 'fallback' };
    } catch (e) {
      return null;
    }
  }

  // Every initialized buy box on the page, so the popup reward bridge can
  // repaint all of them (main box + any duplicated section) at once.
  var BOXES = [];

  function monthsFromPlanName(name) {
    var n = (name || '').toLowerCase();
    if (/15[\s-]*day/.test(n)) return 0.5;             // 15-day plan -> the 15-pack tier
    if (/every\s*other\s*month|2[\s-]*month/.test(n)) return 2;
    if (/3[\s-]*month/.test(n)) return 3;
    if (/every\s*month|1[\s-]*month|monthly/.test(n)) return 1;
    return null;
  }

  function BuyBox(el) {
    this.el = el;
    var dataEl = el.querySelector('[data-buy-box-variants]');
    if (!dataEl) return;

    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('BuyBox: could not parse variant JSON', e);
      return;
    }

    this.formId = el.dataset.formId;
    this.form = document.getElementById(this.formId);
    this.packIndex = parseInt(el.dataset.packIndex, 10) - 1; // 0-based into options[]
    this.packHandle = el.dataset.packHandle;
    this.priceInButton = el.dataset.showPriceInButton !== 'false';
    this.promo = readPromo(el);

    // Async promo resolution for cold visitors (empty cart => Liquid has no
    // promo data). Two steps, both no-ops once a promo is found:
    // 1. Learn the CODE: the sync sources already tried ?promo=, the
    //    discount_code cookie, and sessionStorage; /cart.js is the async
    //    backstop (Shopify associates a /discount/CODE visit with the
    //    session's cart even while it's empty).
    // 2. Learn the VALUE: the metafield map if the code is listed —
    //    otherwise probePromo() asks Shopify's own pricing engine via a
    //    Storefront-API shadow cart, so ANY code (email campaigns, popup
    //    mints, one-offs) renders with zero per-code setup.
    if (!this.promo) {
      var self0 = this;
      var hint0 = '';
      try { hint0 = (new URLSearchParams(window.location.search).get('promo') || '').trim(); } catch (e) { /* ignore */ }
      if (!hint0) hint0 = readCookie('discount_code');
      if (!hint0) { try { hint0 = (sessionStorage.getItem('rd_promo') || '').trim(); } catch (e) { /* ignore */ } }
      if (hint0) this.probePromo(hint0);
      try {
        fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (c) {
            var dc = c && c.discount_codes && c.discount_codes[0] && c.discount_codes[0].code;
            if (!dc || self0.promo) return;
            try { sessionStorage.setItem('rd_promo', dc); } catch (e) { /* ignore */ }
            self0.promo = readPromo(self0.el);
            if (self0.promo) self0.update(false);
            else self0.probePromo(dc);
          })
          .catch(function () { /* network hiccup - box already rendered undiscounted */ });
      } catch (e) { /* fetch unsupported - ignore */ }
    }

    // Compact mode: size panel starts collapsed behind the expander link
    this.compact = el.dataset.compact !== 'false';
    this.optionsWrap = el.querySelector('[data-bb-options]');
    this.expandLink = el.querySelector('[data-bb-expand]');

    this.section = el.closest('[data-section-type="product"]') || document;
    // Enables scoped styling (e.g. the flavor picker's container box) only on
    // pages where the buy box is active.
    if (this.section.classList) this.section.classList.add('buy-box-active');

    // Relocate the "CODE applied" notice so it sits directly ABOVE the main
    // add-to-cart button (below "In stock, ready to ship") instead of mid-box
    // where it gets lost between the purchase cards and the trust list.
    // Keep a direct reference: after the move the note is no longer inside
    // this.el, so renderPromo() must not re-query the buy box for it.
    // Skips the sticky-bar's duplicate button; falls back to leaving the
    // note where the Liquid rendered it if no button is found.
    this.promoNote = el.querySelector('[data-promo-note]');
    if (this.promoNote) {
      var atcBtn = null;
      var atcCands = this.section.querySelectorAll('[data-add-to-cart], button[name="add"]');
      for (var bi = 0; bi < atcCands.length; bi++) {
        if (!atcCands[bi].closest('#sticky-atc')) { atcBtn = atcCands[bi]; break; }
      }
      if (atcBtn && atcBtn.parentNode) {
        this.promoNote.classList.add('buy-box__promo--atc');
        atcBtn.parentNode.insertBefore(this.promoNote, atcBtn);
      }
    }

    // Always-visible purchase cards + supply size boxes
    this.modeCards = Array.prototype.slice.call(el.querySelectorAll('[data-mode-card]'));
    this.sizeBoxes = Array.prototype.slice.call(el.querySelectorAll('[data-size-box]'));
    this.sellingPlanInput = el.querySelector('[data-buy-box-selling-plan]');

    // Optional cross-sell add-on (configured in the block settings)
    this.addon = null;
    this.addonRow = el.querySelector('[data-addon-row]');
    this.addonCheck = el.querySelector('[data-addon-check]');
    this.addonSize = el.querySelector('[data-addon-size]');
    this.addonTierOverride = null; // months key once the shopper picks a size manually
    var addonEl = el.querySelector('[data-buy-box-addon]');
    if (addonEl) {
      try { this.addon = JSON.parse(addonEl.textContent); } catch (e) { this.addon = null; }
      if (this.addon) this.addon.packIndex = this.addon.packIndex - 1; // 0-based
    }

    this.mode = 'sub';          // 'sub' | 'once'
    this.sizeBox = null;        // selected [data-size-box]
    this._syncing = false;
    this._addonAdding = false;

    this.hidePackOptionRow();
    this.bind();
    // From here on update() is safe to call (size boxes, mode cards and the
    // selling-plan input exist). probePromo() can resolve DURING the
    // constructor (sessionStorage cache hit) - it only sets this.promo until
    // this flag is up, and the update() below paints it.
    this._ready = true;
    this.update(false);
    this.decorateFlavorRow();
    this.updateExpressButton();
  }

  BuyBox.prototype = {
    // ---- Shadow-cart promo probe (fully generic promo pricing) ----------
    // Creates a throwaway Storefront-API cart with this product + the code
    // and reads back what Shopify would actually discount. This is
    // Shopify's own pricing engine answering, so it is correct for ANY
    // code with no per-code setup - email campaigns, popup-minted one-offs,
    // whatever. The shadow cart is a separate Storefront-API object; the
    // visitor's real cart is never touched.
    // Guards: only near-integer percentages render (a $10-off fixed code
    // back-computes to e.g. 22.73% and would project wrongly across supply
    // tiers, so it stays hidden - same percentage-only scope as the Liquid
    // path). Product-restricted codes only render on pages where Shopify
    // actually allocated a discount to THIS product. Results (positive and
    // negative) cache for 30 min per code so we probe once, not per page.
    //
    // 10/22/26 fix: Shopify reports an ORDER-level code (every Luv Pop Up
    // LUV-* code, AbsorbMore, most %-off codes) on cart.discountAllocations,
    // NOT on the line - the line's discountAllocations comes back empty even
    // though the code is applicable. The probe used to read the line only, so
    // every order-level code priced at 0% and a cold visitor landing from a
    // /discount/CODE link never saw it. The shadow cart holds exactly one
    // line, so all cart-level allocations belong to it: sum both. Cache key
    // bumped to rd_probe2: so stale 0% results from the old probe are ignored.
    probePromo: function (code) {
      var self = this;
      // Token sources: the shop metafield rendered into data-sf-token, or a
      // token provided at runtime by Customer Luv Pop Up & Quiz
      // (window.CLV_SF_TOKEN / the 'clv:sf-token' event) - whichever
      // arrives first wins; they are interchangeable.
      var token = (this.el.dataset.sfToken || '').trim() || String(window.CLV_SF_TOKEN || '').trim();
      code = String(code || '').trim();
      if (!code) return;
      if (!token) { this._pendingProbe = code; return; }
      if (this.promo || this._probing) return;
      var key = 'rd_probe2:' + code.toUpperCase();
      try {
        var hit = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (hit && hit.exp > Date.now()) {
          if (hit.pct > 0) {
            this.promo = { code: code, pct: hit.pct, source: 'probe' };
            if (this._ready) this.update(false);
          }
          return;
        }
      } catch (e) { /* ignore */ }
      var v = (this.sizeBox && this.variantForPack(this.sizeBox.dataset.pack)) || this.variants[0];
      if (!v || !v.id) return;
      this._probing = true;
      var q = 'mutation($lines:[CartLineInput!]!,$codes:[String!]){cartCreate(input:{lines:$lines,discountCodes:$codes}){cart{discountCodes{code applicable}discountAllocations{discountedAmount{amount}}lines(first:1){nodes{cost{subtotalAmount{amount}}discountAllocations{discountedAmount{amount}}}}}}}';
      try {
        fetch('/api/2024-10/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
          body: JSON.stringify({ query: q, variables: { lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + v.id, quantity: 1 }], codes: [code] } })
        }).then(function (r) { return r.json(); }).then(function (res) {
          self._probing = false;
          var cart = res && res.data && res.data.cartCreate && res.data.cartCreate.cart;
          var line = cart && cart.lines && cart.lines.nodes && cart.lines.nodes[0];
          if (!line) return;
          var sub = parseFloat(line.cost && line.cost.subtotalAmount && line.cost.subtotalAmount.amount) || 0;
          var off = 0;
          var addUp = function (allocs) {
            (allocs || []).forEach(function (a) {
              off += parseFloat(a && a.discountedAmount && a.discountedAmount.amount) || 0;
            });
          };
          addUp(line.discountAllocations);   // product-level codes
          addUp(cart.discountAllocations);   // order-level codes (LUV-*, AbsorbMore, ...)
          var pct = sub > 0 ? (off / sub) * 100 : 0;
          // Near-integer => a real percentage code; anything else is a
          // fixed-amount code we intentionally leave alone.
          var rounded = Math.round(pct);
          var ok = rounded > 0 && rounded < 100 && Math.abs(pct - rounded) < 0.1;
          try { sessionStorage.setItem(key, JSON.stringify({ pct: ok ? rounded : 0, exp: Date.now() + 1800000 })); } catch (e) { /* ignore */ }
          if (ok && !self.promo) {
            self.promo = { code: code, pct: rounded, source: 'probe' };
            if (self._ready) self.update(false);
          }
        }).catch(function () { self._probing = false; });
      } catch (e) { this._probing = false; }
    },

    // The theme's variant picker renders each option as
    // .variant-wrapper > fieldset.variant-input-wrap[data-handle="..."].
    // Hide the whole wrapper for the pack option; the size boxes own it now.
    hidePackOptionRow: function () {
      var fieldset = this.section.querySelector('.variant-input-wrap[data-handle="' + this.packHandle + '"]');
      if (fieldset) {
        var wrapper = fieldset.closest('.variant-wrapper');
        if (wrapper) wrapper.style.display = 'none';
      }
    },

    // The theme picker's own pack radios (visually hidden, still live).
    packRadios: function () {
      var packName = 'option' + (this.packIndex + 1);
      return Array.prototype.slice.call(
        this.section.querySelectorAll('input[data-variant-input][data-index="' + packName + '"]')
      ).filter(function (input) {
        return !input.closest('[data-buy-box]');
      });
    },

    // Current NON-pack option values, read from the theme's live inputs.
    currentOtherOptions: function () {
      var packName = 'option' + (this.packIndex + 1);
      var values = {};
      var inputs = this.section.querySelectorAll('[data-variant-input]');
      Array.prototype.forEach.call(inputs, function (input) {
        var idx = input.dataset.index;
        if (!idx || idx === packName) return;
        if (input.tagName === 'SELECT') {
          values[idx] = input.value;
        } else if (input.checked) {
          values[idx] = input.value;
        }
      });
      return values;
    },

    variantForPack: function (packValue) {
      var packIndex = this.packIndex;
      var others = this.currentOtherOptions();
      return this.variants.filter(function (v) {
        if (!v.available) return false;
        if (v.options[packIndex] !== packValue) return false;
        for (var idx in others) {
          var i = parseInt(idx.replace('option', ''), 10) - 1;
          if (others[idx] && v.options[i] !== others[idx]) return false;
        }
        return true;
      })[0] || null;
    },

    planFor: function (variant, months) {
      if (!variant) return null;
      return (variant.plans || []).filter(function (p) {
        return monthsFromPlanName(p.name) === months;
      })[0] || null;
    },

    // Baseline for SAVE % and strikethroughs: compare-at when it beats price,
    // otherwise the one-time price.
    baselineFor: function (variant) {
      if (!variant) return 0;
      return (variant.compare_at_price && variant.compare_at_price > variant.price)
        ? variant.compare_at_price
        : variant.price;
    },

    bind: function () {
      var self = this;

      this.sizeBoxes.forEach(function (box) {
        box.addEventListener('click', function () {
          if (box.hidden) return;
          self.sizeBox = box;
          self.update(true);
        });
      });

      this.modeCards.forEach(function (card) {
        card.addEventListener('click', function () {
          if (card.hidden) return;
          self.setMode(card.dataset.modeCard, true);
        });
      });

      this.bindAddonCartAdd();
      this.bindBenefitsLink();

      // Expander link toggles the supply-size panel
      if (this.compact && this.optionsWrap && this.expandLink) {
        this.expandLink.addEventListener('click', function (evt) {
          evt.preventDefault();
          var opening = self.optionsWrap.hidden;
          self.optionsWrap.hidden = !opening;
          self.expandLink.classList.toggle('buy-box__more--open', opening);
        });
      }

      // When the theme's variant engine fires (e.g. flavor change), re-render
      // prices/availability and re-apply the selection. Ignore events we
      // caused ourselves.
      this.section.addEventListener('variantChange', function () {
        if (self._syncing) return;
        self.update(false);
      });

      // Because we boot before the theme's deferred bundle, re-fire the
      // variant sync once everything else has loaded so theme.Variants
      // catches up (images, price block, history state).
      var resync = function () {
        if (self.sizeBox) self.syncThemeVariant(self.sizeBox.dataset.pack);
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', resync);
      }
      if (document.readyState !== 'complete') {
        window.addEventListener('load', resync);
      }

      // Submit guard (capture phase, runs before the theme's AJAX handler
      // serializes the form): force id + selling_plan to the buy box's
      // current selection so a mismatched add can never reach /cart/add.
      if (this.form) {
        this.form.addEventListener('submit', function () {
          if (!self.sizeBox) return;
          var variant = self.variantForPack(self.sizeBox.dataset.pack);
          var master = self.form.querySelector('select[name="id"], input[name="id"]');
          if (variant && master) master.value = variant.id;
          if (self.sellingPlanInput) {
            self.sellingPlanInput.value = self.mode === 'sub' ? (self.sizeBox.dataset.planId || '') : '';
          }
        }, true);
      }
    },

    setMode: function (mode, userInitiated) {
      if (mode !== 'sub' && mode !== 'once') return;
      if (mode === this.mode) return;
      this.mode = mode;

      this.el.classList.toggle('buy-box--once', mode === 'once');

      // Shipping perk reads "always" for subscribers, "today" for one-time
      var shipText = this.el.querySelector('[data-trust1-text]');
      if (shipText) {
        shipText.textContent = mode === 'once' ? shipText.dataset.textOnce : shipText.dataset.textSub;
      }

      this.update(userInitiated);
    },

    currentMonths: function () {
      return this.sizeBox ? parseFloat(this.sizeBox.dataset.months) : 1;
    },

    // ---- Supply size boxes: availability + SAVE chips (60/90 only) ----
    // Apply the active order-wide percentage code to a price in cents.
    // No promo, or anything unexpected, returns the price untouched.
    promoPrice: function (cents) {
      if (!this.promo || !cents || cents < 0) return cents;
      // Mirror Shopify's own rounding: it floors the discount amount, then
      // subtracts. Verified against a live cart - $46.74 at 20% allocated
      // 934c (not 935), landing on $37.40. Rounding the price instead would
      // under-quote by a cent and the PDP would promise less than checkout.
      var off = Math.floor(cents * this.promo.pct / 100);
      var out = cents - off;
      return (out > 0 && out < cents) ? out : cents;
    },

    // The "CODE applied" notice. Always names the renewal price in sub mode so
    // a first-order discount is never mistaken for the ongoing price.
    // The mini "CODE APPLIED" chip beside the "How should it arrive?" label.
    // Same visibility rules as the green box. When the label text is blank
    // (data-modes-label-empty) the whole row toggles with the chip so an
    // empty flex row never reserves space.
    setPromoMini: function (on) {
      var mini = this.el.querySelector('[data-promo-mini]');
      if (!mini) return;
      if (on) {
        var mc = mini.querySelector('[data-promo-mini-code]');
        if (mc) mc.textContent = this.promo.code;
      }
      mini.hidden = !on;
      var row = mini.closest('.buy-box__modes-label');
      if (row && row.hasAttribute('data-modes-label-empty')) row.hidden = !on;
    },

    renderPromo: function () {
      // this.promoNote survives the relocation next to the ATC button;
      // querying this.el would miss it after the move.
      var note = this.promoNote || this.el.querySelector('[data-promo-note]');
      if (!note) return;
      if (!this.promo || !this.sizeBox) { note.hidden = true; this.setPromoMini(false); return; }

      var codeEl = note.querySelector('[data-promo-note-code]');
      var textEl = note.querySelector('[data-promo-note-text]');
      if (codeEl) codeEl.textContent = this.promo.code;

      // Savings are measured against the SAME baseline the size cards use
      // (baselineFor = compare-at when it beats price), so the figures here
      // reconcile with the SAVE % chip above instead of introducing a new
      // number. Sub mode names the renewal price so a first-order discount is
      // never mistaken for the ongoing price. The leading em dash was replaced
      // with a colon (01AUG2026) - at small sizes the dash read as a minus.
      var variant = this.variantForPack(this.sizeBox.dataset.pack);
      var plan = this.planFor(variant, this.currentMonths());
      var baseline = this.baselineFor(variant);

      if (textEl) {
        if (this.mode === 'sub' && plan) {
          var today = this.promoPrice(plan.price);
          var saved = baseline - today;
          textEl.innerHTML = formatMoney(today) + ' today'
            + (saved > 0 ? ' (save ' + formatMoney(saved) + ')' : '')
            + ', then ' + formatMoney(plan.price) + ' per delivery.';
        } else if (variant) {
          var net = this.promoPrice(variant.price);
          var off = baseline - net;
          if (off <= 0) { note.hidden = true; this.setPromoMini(false); return; }
          var pct = baseline > net ? Math.round((1 - net / baseline) * 100) : 0;
          textEl.innerHTML = "You're saving " + formatMoney(off)
            + (pct > 0 ? ' (-' + pct + '%)' : '') + ' on this order!';
        } else {
          note.hidden = true;
          this.setPromoMini(false);
          return;
        }
      }
      note.hidden = false;
      this.setPromoMini(true);
    },

    renderSizes: function () {
      var self = this;
      var anyVisible = false;

      this.sizeBoxes.forEach(function (box) {
        var months = parseFloat(box.dataset.months);
        var variant = self.variantForPack(box.dataset.pack);
        var plan = self.planFor(variant, months);

        // In sub mode a size needs a matching cadence plan; one-time just a variant.
        if (!variant || (self.mode === 'sub' && !plan)) {
          box.hidden = true;
          return;
        }

        anyVisible = true;
        box.hidden = false;
        box.dataset.variantId = variant.id;
        box.dataset.planId = plan ? plan.id : '';

        // Bulk SAVE chips on the larger sizes, from live prices
        var badgeEl = box.querySelector('[data-size-badge]');
        var badgePctEl = box.querySelector('[data-size-badge-pct]');
        if (badgeEl && badgePctEl) {
          var effectivePrice = self.promoPrice((self.mode === 'sub' && plan) ? plan.price : variant.price);
          var baseline = self.baselineFor(variant);
          var pct = baseline > effectivePrice ? Math.round((1 - effectivePrice / baseline) * 100) : 0;
          if (pct > 0) {
            badgePctEl.textContent = pct + '%';
            applySaveDepth(badgeEl, pct);
            badgeEl.hidden = false;
          } else {
            badgeEl.hidden = true;
          }
        }
      });

      this.el.classList.toggle('buy-box--empty', !anyVisible);
    },

    // Keep the current size if still available, otherwise fall back to the
    // first visible one.
    ensureSelection: function () {
      if (this.sizeBox && !this.sizeBox.hidden) return;
      var visible = this.sizeBoxes.filter(function (b) { return !b.hidden; });
      this.sizeBox = visible.filter(function (b) { return b.dataset.defaultSize !== undefined; })[0] || visible[0] || null;
    },

    // ---- The two always-visible purchase cards ----
    renderModeCards: function () {
      var self = this;
      var box = this.sizeBox;
      if (!box) return;

      var months = this.currentMonths();
      var variant = this.variantForPack(box.dataset.pack);
      var plan = this.planFor(variant, months);
      var baseline = this.baselineFor(variant);

      this.modeCards.forEach(function (card) {
        var isSub = card.dataset.modeCard === 'sub';

        // No cadence plan for this size -> subscribing isn't possible here
        if (isSub && !plan) {
          card.hidden = true;
          if (self.mode === 'sub') {
            self.mode = 'once';
            self.el.classList.add('buy-box--once');
            var shipText = self.el.querySelector('[data-trust1-text]');
            if (shipText) shipText.textContent = shipText.dataset.textOnce;
          }
          return;
        }
        card.hidden = false;

        var effectivePrice = self.promoPrice(isSub ? plan.price : (variant ? variant.price : 0));

        var descEl = card.querySelector('[data-mode-desc]');
        if (descEl) descEl.textContent = isSub ? box.dataset.descSub : box.dataset.descOnce;

        card.querySelector('[data-mode-price]').innerHTML = formatMoney(effectivePrice);

        var compareEl = card.querySelector('[data-mode-compare]');
        var compareAmountEl = card.querySelector('[data-mode-compare-amount]');
        var badgeEl = card.querySelector('[data-mode-badge]');
        var badgePctEl = card.querySelector('[data-mode-badge-pct]');

        if (baseline > effectivePrice) {
          compareAmountEl.innerHTML = formatMoney(baseline);
          compareEl.hidden = false;
          var pct = Math.round((1 - effectivePrice / baseline) * 100);
          if (pct > 0 && badgeEl) {
            badgePctEl.textContent = pct + '%';
            applySaveDepth(badgeEl, pct);
            badgeEl.hidden = false;
          } else if (badgeEl) {
            badgeEl.hidden = true;
          }
        } else {
          compareEl.hidden = true;
          if (badgeEl) badgeEl.hidden = true;
        }

        var on = (card.dataset.modeCard === self.mode);
        card.classList.toggle('buy-box__card--selected', on);
        card.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    },

    // Check the theme picker's pack radio + fire change so theme.Variants
    // updates the master select, price, images, inventory and URL.
    syncThemeVariant: function (packValue) {
      var radio = this.packRadios().filter(function (r) { return r.value === packValue; })[0];
      if (radio) {
        this._syncing = true;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        this._syncing = false;
      }

      // RACE GUARD: buy-box boots early (async, at readystatechange), often
      // BEFORE the theme's deferred bundle wires theme.Variants. The radio
      // change above then lands on no listener and the form's master
      // select[name=id] stays on the product's FIRST AVAILABLE variant
      // (the 15-pack on His/Her) while the selling plan targets the 30-pack
      // -> Shopify 422 "selling plan not valid" -> empty cart. Write the
      // variant id into the form directly so the posted id NEVER depends on
      // theme.js timing. theme.Variants still repaints images/price when it
      // wakes up (the re-sync in bind() re-fires the change for it).
      var variant = this.variantForPack(packValue);
      if (variant && this.form) {
        var master = this.form.querySelector('select[name="id"], input[name="id"]');
        if (master && String(master.value) !== String(variant.id)) {
          master.value = variant.id;
        }
      }
    },

    // ---- Single pipeline: sizes -> selection -> cards -> form state ----
    update: function (userInitiated) {
      this.renderSizes();
      this.ensureSelection();
      var box = this.sizeBox;
      if (!box) return;

      this.renderModeCards();

      this.sizeBoxes.forEach(function (b) {
        var on = b === box;
        b.classList.toggle('buy-box__size--selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });

      this.syncThemeVariant(box.dataset.pack);

      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = this.mode === 'sub' ? (box.dataset.planId || '') : '';
      }

      this.updateAddon();
      this.updateCartButton();
      this.updateTrustSave();
      this.renderPromo();
      this.updateExpressButton();

      if (userInitiated) {
        this.el.dispatchEvent(new CustomEvent('buyBox:planChange', {
          bubbles: true,
          detail: { mode: this.mode, planId: box.dataset.planId, variantId: box.dataset.variantId }
        }));
      }
    },

    // ---- Cross-sell add-on: match the partner product to the selected
    // cadence/tier (90-pack main -> 90-pack add-on, same subscription). ----
    addonSelection: function () {
      if (!this.addon || !this.addonRow || !this.sizeBox) return null;

      var months = this.addonTierOverride !== null
        ? parseFloat(this.addonTierOverride)
        : this.currentMonths();
      var tierPack = this.addon.tiers[String(months)] || this.addon.tiers['1'];
      if (!tierPack) return null;
      if (this.addonTierOverride === null && !this.addon.tiers[String(months)]) months = 1;

      var packIndex = this.addon.packIndex;

      // Prefer the add-on variant that shares a flavor with the shopper's
      // current selection (e.g. Bright-Strawberry Her -> Bright-Strawberry
      // His); fall back to the tier's first available variant.
      var mainVals = [];
      var others = this.currentOtherOptions();
      for (var key in others) {
        if (others[key]) mainVals.push(String(others[key]).toLowerCase());
      }
      var pickForPack = function (variants, pack) {
        var candidates = variants.filter(function (v) {
          return v.available && v.options[packIndex] === pack;
        });
        if (!candidates.length) return null;
        var flavorMatch = candidates.filter(function (v) {
          return v.options.some(function (opt, i) {
            return i !== packIndex && mainVals.indexOf(String(opt).toLowerCase()) !== -1;
          });
        })[0];
        return flavorMatch || candidates[0];
      };

      var variant = pickForPack(this.addon.variants, tierPack);
      // Fall back to the add-on's 1-month tier if the matching tier is unavailable
      if (!variant && this.addon.tiers['1'] && tierPack !== this.addon.tiers['1']) {
        months = 1;
        variant = pickForPack(this.addon.variants, this.addon.tiers['1']);
      }
      if (!variant) return null;

      var plan = null;
      if (this.mode === 'sub') {
        plan = (variant.plans || []).filter(function (p) {
          return monthsFromPlanName(p.name) === months;
        })[0] || null;
      }

      return {
        variant: variant,
        plan: plan,
        pack: variant.options[packIndex],
        price: plan ? plan.price : variant.price,
        baseline: this.baselineFor(variant)
      };
    },

    updateAddon: function () {
      if (!this.addonRow) return;

      var sel = this.addonSelection();
      if (!sel) {
        this.addonRow.hidden = true;
        return;
      }

      this.addonRow.hidden = false;
      this.addonRow.dataset.variantId = sel.variant.id;
      this.addonRow.dataset.planId = sel.plan ? sel.plan.id : '';
      this.addonRow.dataset.price = sel.price;
      this.addonRow.dataset.baseline = sel.baseline;

      this.addonRow.querySelector('[data-addon-price]').innerHTML = formatMoney(sel.price);

      // Pack size, e.g. "90 Pack" — mirrors the shopper's selected tier
      // (or their manual dropdown pick)
      var packEl = this.addonRow.querySelector('[data-addon-pack]');
      if (packEl) packEl.textContent = sel.pack;
      if (this.addonSize) {
        var key = null;
        for (var k in this.addon.tiers) {
          if (this.addon.tiers[k] === sel.pack) { key = k; break; }
        }
        if (key !== null && this.addonSize.value !== key) this.addonSize.value = key;
      }

      var compareEl = this.addonRow.querySelector('[data-addon-compare]');
      var compareAmountEl = this.addonRow.querySelector('[data-addon-compare-amount]');
      var badgeEl = this.addonRow.querySelector('[data-addon-badge]');
      var badgePctEl = this.addonRow.querySelector('[data-addon-badge-pct]');

      if (sel.baseline > sel.price) {
        if (compareEl) {
          compareAmountEl.innerHTML = formatMoney(sel.baseline);
          compareEl.hidden = false;
        }
        var pct = Math.round((1 - sel.price / sel.baseline) * 100);
        if (badgeEl && pct > 0) {
          badgePctEl.textContent = pct + '%';
          badgeEl.hidden = false;
        } else if (badgeEl) {
          badgeEl.hidden = true;
        }
      } else {
        if (compareEl) compareEl.hidden = true;
        if (badgeEl) badgeEl.hidden = true;
      }
    },

    // After the MAIN product is added to the cart, chain the add-on into the
    // same cart (same selling-plan cadence). The theme's own AJAX add fires
    // ajaxProduct:added, but cart apps (e.g. UpCart) intercept Add to Cart
    // with their OWN fetch/XHR and never fire that event — so we also sniff
    // successful /cart/add requests at the network level. Both paths funnel
    // into one deduped chain() call.
    bindAddonCartAdd: function () {
      var self = this;
      if (!this.addonRow || !this.addonCheck) return;

      // Don't let the Learn More link toggle the checkbox
      var link = this.addonRow.querySelector('[data-addon-link]');
      if (link) {
        link.addEventListener('click', function (evt) { evt.stopPropagation(); });
      }

      // Size dropdown: options come from the add-on's real tiers; picking one
      // overrides the mirror-the-main-selection default until changed again.
      if (this.addonSize && this.addon) {
        var packIndex = this.addon.packIndex;
        var variants = this.addon.variants;
        ['0.5', '1', '2', '3'].forEach(function (key) {
          var pack = self.addon.tiers[key];
          if (!pack) return;
          var exists = variants.some(function (v) { return v.available && v.options[packIndex] === pack; });
          if (!exists) return;
          var opt = document.createElement('option');
          opt.value = key;
          opt.textContent = pack;
          self.addonSize.appendChild(opt);
        });
        this.addonSize.addEventListener('click', function (evt) { evt.stopPropagation(); });
        this.addonSize.addEventListener('change', function (evt) {
          evt.stopPropagation();
          self.addonTierOverride = self.addonSize.value;
          self.updateAddon();
          self.updateCartButton();
          self.updateExpressButton();
        });
      }

      this.addonCheck.addEventListener('change', function () {
        self.updateCartButton();
        // Express checkout (Buy now / wallets) bypasses the cart, so it can't
        // carry the add-on — hide it while the add-on box is ticked.
        self.updateExpressButton();
      });

      var lastChain = 0;
      var chain = function (mainProductId) {
        if (self._addonAdding) return;
        if (!self.addonCheck.checked || self.addonRow.hidden) return;
        // Only chain when the added item is THIS page's product (quick-shops
        // and carousels elsewhere on the page must not trigger it)
        if (mainProductId && String(mainProductId) !== String(self.el.dataset.productId)) return;
        // One chain per main add, even if the event AND the sniffer both fire
        var now = Date.now();
        if (now - lastChain < 1500) return;
        lastChain = now;

        self._addonAdding = true;
        var body = { id: Number(self.addonRow.dataset.variantId), quantity: 1 };
        if (self.mode === 'sub' && self.addonRow.dataset.planId) {
          body.selling_plan = self.addonRow.dataset.planId;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        })
        .then(function (r) { return r.json(); })
        .then(function (item) {
          // Rebuild whatever cart drawer is running with the add-on included
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: item, addToCartBtn: null }
          }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        })
        .catch(function (e) { console.warn('BuyBox: add-on cart add failed', e); })
        .finally(function () { self._addonAdding = false; });
      };

      // Path 1: the theme's own AJAX add
      document.addEventListener('ajaxProduct:added', function (evt) {
        var added = evt.detail && evt.detail.product;
        chain(added && added.product_id);
      });

      // Path 2: network-level sniffing of /cart/add for cart apps.
      // Patched once per page; every buy box registers its chain hook.
      if (!window._bbCartAddHooks) {
        window._bbCartAddHooks = [];

        var isCartAdd = function (url) {
          return typeof url === 'string' && /\/cart\/add(\.js)?([?]|$)/.test(url.split('#')[0]);
        };
        var notify = function (productId) {
          window._bbCartAddHooks.forEach(function (fn) {
            try { fn(productId); } catch (e) { /* noop */ }
          });
        };
        var pidFrom = function (json) {
          if (!json) return null;
          if (json.product_id) return json.product_id;
          if (json.items && json.items[0]) return json.items[0].product_id;
          return null;
        };

        var origFetch = window.fetch;
        window.fetch = function () {
          var input = arguments[0];
          var url = (input && input.url) || input;
          var promise = origFetch.apply(this, arguments);
          if (isCartAdd(url)) {
            promise.then(function (res) {
              if (res && res.ok) {
                res.clone().json()
                  .then(function (j) { notify(pidFrom(j)); })
                  .catch(function () { notify(null); });
              }
            }).catch(function () { /* noop */ });
          }
          return promise;
        };

        var origOpen = window.XMLHttpRequest.prototype.open;
        var origSend = window.XMLHttpRequest.prototype.send;
        window.XMLHttpRequest.prototype.open = function (method, url) {
          this._bbCartAdd = isCartAdd(url);
          return origOpen.apply(this, arguments);
        };
        window.XMLHttpRequest.prototype.send = function () {
          if (this._bbCartAdd) {
            this.addEventListener('load', function () {
              if (this.status >= 200 && this.status < 300) {
                var pid = null;
                try { pid = pidFrom(JSON.parse(this.responseText)); } catch (e) { /* noop */ }
                notify(pid);
              }
            });
          }
          return origSend.apply(this, arguments);
        };
      }
      window._bbCartAddHooks.push(chain);
    },

    // "See the full benefits" links (the timeline block lives OUTSIDE this
    // widget, below the buy buttons) open the page's existing "Why ... Love
    // Their Root'd" flyout drawer. Auto-detects the drawer trigger; if the
    // page has no drawers, the link hides itself and the text stands alone.
    bindBenefitsLink: function () {
      document.querySelectorAll('[data-benefits-open]').forEach(function (link) {
        if (link.dataset.benefitsBound) return;
        link.dataset.benefitsBound = 'true';

        var target = link.dataset.drawerTarget;
        var trigger = target
          ? document.querySelector('[data-rt-drawer-open="' + target + '"]')
          : document.querySelector('[data-rt-drawer-open^="why-"]');

        if (!trigger) {
          // Timeline-style links hide; inline trust links fall back to text
          if (link.classList.contains('buy-box__trust-link')) {
            var span = document.createElement('span');
            span.innerHTML = link.innerHTML;
            link.replaceWith(span);
          } else {
            link.style.display = 'none';
          }
          return;
        }

        link.addEventListener('click', function (evt) {
          evt.preventDefault();
          trigger.click();
        });
      });
    },

    // Keep the "SAVE X% on every refill" trust badge in sync with the
    // selected size's subscription discount (vs compare-at). In one-time mode
    // the crossed-out number shows exactly what the shopper is giving up.
    updateTrustSave: function () {
      var el = this.el.querySelector('[data-trust-save]');
      if (!el || !this.sizeBox) return;

      var variant = this.variantForPack(this.sizeBox.dataset.pack);
      var plan = this.planFor(variant, this.currentMonths());

      if (!variant || !plan) return; // keep whatever text is there

      var baseline = this.baselineFor(variant);
      var pct = baseline > plan.price ? Math.round((1 - plan.price / baseline) * 100) : 0;
      if (pct > 0) {
        el.textContent = 'SAVE ' + pct + '%';
      }

      // This line is the RECURRING saving, deliberately not discounted by the
      // first-order code. With a code active the card above shows a bigger
      // number, so "forever" alone reads as a contradiction - "on every
      // renewal" explains the gap. Restores itself when no code is in play.
      var textEl = this.el.querySelector('[data-trust-save-text]');
      if (textEl) {
        if (!textEl.dataset.original) textEl.dataset.original = textEl.textContent.trim();
        textEl.textContent = this.promo ? 'on every renewal' : textEl.dataset.original;
      }
    },

    // Mirror the effective price into the Add to Cart button text, e.g.
    // "Start Now — $116.82  $177.00" (compare-at crossed out, small + grey).
    // Skips sold-out states.
    updateCartButton: function () {
      if (!this.priceInButton || !this.sizeBox) return;

      var textEl = this.section.querySelector('[data-add-to-cart-text]');
      if (!textEl) return;

      var variant = this.variantForPack(this.sizeBox.dataset.pack);
      if (!variant) return;

      var plan = this.planFor(variant, this.currentMonths());
      var effectivePrice = (this.mode === 'sub' && plan) ? plan.price : variant.price;
      var baseline = this.baselineFor(variant);

      // Include the add-on in the button total when its box is ticked
      if (this.addonCheck && this.addonCheck.checked && this.addonRow && !this.addonRow.hidden) {
        effectivePrice += parseInt(this.addonRow.dataset.price || '0', 10);
        baseline += parseInt(this.addonRow.dataset.baseline || '0', 10);
      }

      // An order-wide code discounts the add-on too, so apply it last.
      effectivePrice = this.promoPrice(effectivePrice);

      if (!textEl.dataset.defaultText) textEl.dataset.defaultText = textEl.textContent.trim();
      // Per-mode labels ("Start Now" for subscriptions, "Add to Cart" one-time)
      var labelOverride = this.mode === 'sub' ? this.el.dataset.labelSub : this.el.dataset.labelOnce;
      var defaultText = labelOverride || textEl.dataset.defaultText;

      var html = defaultText + ' — ' + formatMoney(effectivePrice);
      if (baseline > effectivePrice) {
        html += ' <s class="buy-box__btn-compare">' + formatMoney(baseline) + '</s>';
      }
      textEl.innerHTML = html;

      // Mirror the live selection into the sticky add-to-cart bar (replaces
      // its static "From ..." Liquid fallback with the real selected price).
      var stickyPrice = document.querySelector('#sticky-atc .sticky-price');
      if (stickyPrice) {
        var label = this.mode === 'sub'
          ? (this.sizeBox.dataset.labelSub || '')
          : (this.sizeBox.dataset.pack + ' · one-time');
        var sHtml = '';
        if (baseline > effectivePrice) {
          sHtml += '<span style="text-decoration: line-through; opacity: 0.7;">' + formatMoney(baseline) + '</span>&nbsp;';
        }
        sHtml += '<strong>' + formatMoney(effectivePrice) + '</strong>';
        if (label) sHtml += ' <span style="opacity:.75;">&middot; ' + label + '</span>';
        stickyPrice.innerHTML = sHtml;
      }
    },

    // ---- Flavor emojis: prefix each existing swatch label with its emoji.
    // The picker stays fully visible at all times; nothing collapses. ----
    decorateFlavorRow: function () {
      var map = {};
      (this.el.dataset.flavorEmojis || '').split(/[,\n]/).forEach(function (pair) {
        var i = pair.lastIndexOf(':');
        if (i > 0) map[pair.slice(0, i).trim().toLowerCase().replace(/-/g, ' ')] = pair.slice(i + 1).trim();
      });
      var emojiFor = function (value) {
        var v = (value || '').toLowerCase().replace(/-/g, ' ');
        for (var k in map) { if (v.indexOf(k) !== -1) return map[k]; }
        return '';
      };

      Array.prototype.slice.call(this.section.querySelectorAll('.variant-wrapper')).forEach(function (wrapper) {
        if (wrapper.style.display === 'none' || wrapper.dataset.bbEmojis) return;
        wrapper.dataset.bbEmojis = 'true';

        Array.prototype.slice.call(wrapper.querySelectorAll('input[type="radio"][data-variant-input]')).forEach(function (input) {
          var em = emojiFor(input.value);
          if (!em) return;
          var label = (input.id && wrapper.querySelector('label[for="' + input.id + '"]')) || input.closest('label');
          if (!label || label.querySelector('.bb-flavor-emoji')) return;
          var span = document.createElement('span');
          span.className = 'bb-flavor-emoji';
          span.textContent = em + ' ';
          label.insertBefore(span, label.firstChild);
        });
      });
    },


    // ---- Express checkout (Buy now / Apple Pay / Google Pay) can't carry
    // the chained add-on, so hide it while the add-on box is ticked. ----
    updateExpressButton: function () {
      var btn = this.section.querySelector('.shopify-payment-button');
      if (!btn) return;
      var addonOn = this.addonCheck && this.addonCheck.checked && this.addonRow && !this.addonRow.hidden;
      btn.style.display = addonOn ? 'none' : '';
    }
  };

  // ---- Gallery self-heal ----
  // Rarely (mostly iOS Safari, slow networks, or back-forward cache restores)
  // the product gallery's slider measures itself before its images have any
  // height and collapses to 0 — page renders with no main image. If the
  // gallery is collapsed after load, kick lazysizes + the slider to
  // re-measure. Retries a few times, then leaves things alone.
  function healGallery() {
    var gallery = document.querySelector('.product__main-photos');
    if (!gallery) return;

    var heal = function (attempt) {
      if (gallery.offsetHeight > 60) return; // healthy, nothing to do

      try {
        if (window.lazySizes && window.lazySizes.loader) window.lazySizes.loader.checkElems();
      } catch (e) { /* noop */ }

      var slider = gallery.querySelector('.flickity-enabled');
      if (slider && window.Flickity) {
        try {
          var flkty = window.Flickity.data(slider);
          if (flkty) flkty.resize();
        } catch (e) { /* noop */ }
      }
      window.dispatchEvent(new Event('resize'));

      if (attempt < 3) window.setTimeout(function () { heal(attempt + 1); }, 800);
    };

    window.setTimeout(function () { heal(0); }, 600);
    window.addEventListener('pageshow', function (evt) {
      if (evt.persisted) window.setTimeout(function () { heal(0); }, 150);
    });
  }

  function init() {
    healGallery();
    document.querySelectorAll('[data-buy-box]').forEach(function (el) {
      if (!el.dataset.buyBoxReady) {
        el.dataset.buyBoxReady = 'true';
        BOXES.push(new BuyBox(el));
      }
    });
  }

  // Late-arriving Storefront token from Customer Luv Pop Up & Quiz: the
  // app embed loads its config async, so its token can land after the buy
  // box booted. Any probe that was waiting on a token fires now.
  window.addEventListener('clv:sf-token', function (ev) {
    var t = ev && ev.detail && ev.detail.token;
    if (t && !window.CLV_SF_TOKEN) window.CLV_SF_TOKEN = String(t);
    BOXES.forEach(function (b) {
      if (b._pendingProbe && !b.promo) {
        var c = b._pendingProbe;
        b._pendingProbe = '';
        b.probePromo(c);
      }
    });
  });

  // Live bridge from Customer Luv Pop Up & Quiz: the popup dispatches
  // 'clv:reward' the moment a visitor claims a percentage code, and every
  // buy box on the page repaints IMMEDIATELY - no refresh, no metafield
  // entry, any unique minted code. When the code expires we revert to
  // whatever the other promo sources say.
  window.addEventListener('clv:reward', function (ev) {
    var d = (ev && ev.detail) || {};
    var pct = parseFloat(d.pct);
    if (!d.code || !(pct > 0 && pct < 100)) return;
    if (d.exp && d.exp <= Date.now()) return;
    BOXES.forEach(function (b) {
      b.promo = { code: String(d.code), pct: pct, source: 'reward' };
      b.update(false);
    });
    if (d.exp) {
      var ms = d.exp - Date.now();
      if (ms > 0 && ms < 864e5) {
        window.setTimeout(function () {
          try { sessionStorage.removeItem('clv_reward'); } catch (e) { /* ignore */ }
          BOXES.forEach(function (b) {
            if (b.promo && b.promo.source === 'reward') {
              b.promo = readPromo(b.el);
              b.update(false);
            }
          });
        }, ms + 1000);
      }
    }
  });

  // Boot as EARLY as possible. The script tag is `async` (was `defer`), so
  // we usually execute the moment the file arrives. If the DOM is still
  // parsing, readystatechange -> 'interactive' fires the instant parsing
  // finishes — BEFORE the theme's deferred vendor/theme bundles run — so
  // the buy box no longer queues behind them. DOMContentLoaded stays as a
  // safety net; init() is idempotent via data-buy-box-ready.
  if (document.readyState === 'loading') {
    document.addEventListener('readystatechange', function onRS() {
      if (document.readyState !== 'loading') {
        document.removeEventListener('readystatechange', onRS);
        init();
      }
    });
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init inside the theme editor when sections reload
  document.addEventListener('shopify:section:load', init);
})();

/* ---- Root'd fact chips: scroll-to-and-open the Supplement Facts tab (moved from fact-chips.liquid) ---- */
(function () {
  function init() {
    var links = document.querySelectorAll('[data-rtd-facts-jump]');
    links.forEach(function (a) {
      if (a.getAttribute('data-rtd-init') === '1') return;
      a.setAttribute('data-rtd-init', '1');
      a.addEventListener('click', function (e) {
        e.preventDefault();
        var tabs = document.querySelectorAll('.product-block--tab');
        for (var i = 0; i < tabs.length; i++) {
          if (/supplement\s*facts/i.test(tabs[i].textContent || '')) {
            tabs[i].scrollIntoView({ behavior: 'smooth', block: 'start' });
            var t = tabs[i].querySelector('button, summary, .collapsible-trigger');
            if (t && t.getAttribute('aria-expanded') !== 'true') t.click();
            return;
          }
        }
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
